import { AuthResponse, ImpersonatorInfo } from '../types/authTypes';

const SESSION_KEYS = [
  'sessionToken',
  'userId',
  'role',
  'username',
  'fullName',
  'guid',
  'startPage',
  'belongsStorage',
  'cityId',
  'pharmacyNum',
  'deviceInfo',
  'forceProfileCheck',
  'new_document_autosave',
  'onlineJournalActiveTab',
  'onlineJournalTab2ExpiryMonth',
  'periodReportData',
  'userCheckData',
  'userAvatar',
  'impersonated_by',
  'impersonator_info',
  'is_impersonated'
];

/**
 * Очищает все связанные с текущей сессией данные из localStorage.
 */
export const clearUserSession = (): void => {
  SESSION_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });
  sessionStorage.removeItem('openProfileOnLogin');
};

// Данные учетной записи, которые saveUserSession пишет только при наличии значения, а профиль пересчитывает позже:
// без очистки они переходят от одной учетной записи к другой
const ACCOUNT_DATA_KEYS = [
  'pos',
  'reports',
  'myPharmacy',
  'targetInterface',
  'roleLevel',
  'roleName',
  'permissions',
  'roleBadge',
  'profileIncomplete',
  'isPersonalIncomplete',
  'isMailIncomplete',
  'isSyncIncomplete',
  'staffLogin',
  'staffInfo'
];

/**
 * Полная очистка данных учетной записи: то же, что clearUserSession, плюс права, ранг и флаги профиля.
 * Нужна при смене аккаунта, выходе и переходе в режим «вход под сотрудником» и обратно.
 */
export const clearUserSessionFully = (): void => {
  clearUserSession();
  ACCOUNT_DATA_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });
};

// «Вход под сотрудником»: снимок учетной записи администратора и раздел, в который он вернется
const IMPERSONATION_ORIGINAL_KEY = 'impersonation_original_session';
const IMPERSONATION_RETURN_KEY = 'impersonation_return_path';
const IMPERSONATION_MODE_KEYS = ['impersonated_by', 'impersonator_info', 'is_impersonated'];

// В снимок не входят ключи самого режима и аватар (data URL до 300 КБ): шапка профиля загружает его заново
const ADMIN_SNAPSHOT_KEYS = SESSION_KEYS.concat(ACCOUNT_DATA_KEYS).filter(
  (key) => key !== 'userAvatar' && IMPERSONATION_MODE_KEYS.indexOf(key) === -1
);

/**
 * Куда вести после входа: страницу определяет интерфейс роли с сервера, а не название роли
 */
export const getHomeRoute = (data: { targetInterface?: string }): string => {
  return data.targetInterface === 'admin' ? '/admin' : '/dashboard';
};

/**
 * Получение текущего токена сессии
 */
export const getSessionToken = (): string | null => {
  return localStorage.getItem('sessionToken');
};

/**
 * Проверка, находится ли пользователь в режиме входа под другим пользователем (impersonation)
 */
export const isImpersonating = (): boolean => {
  return localStorage.getItem('is_impersonated') === 'true';
};

/**
 * Получение метаданных администратора при имперсонации
 */
export const getImpersonatorInfo = (): ImpersonatorInfo | null => {
  const raw = localStorage.getItem('impersonator_info');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Сохранение данных авторизованного пользователя в localStorage
 */
export const saveUserSession = (data: AuthResponse): void => {
  if (data.sessionToken) {
    localStorage.setItem('sessionToken', data.sessionToken);
  }
  localStorage.setItem('userId', String(data.userId));
  localStorage.setItem('role', data.role);
  localStorage.setItem('username', data.username);
  if (data.fullName) localStorage.setItem('fullName', data.fullName);
  if ((data as any).avatar) localStorage.setItem('userAvatar', (data as any).avatar);
  if (data.guid) localStorage.setItem('guid', data.guid);
  if ((data as any).pharmacyNum) localStorage.setItem('pharmacyNum', String((data as any).pharmacyNum));
  if ((data as any).pos !== undefined) localStorage.setItem('pos', String((data as any).pos));
  if ((data as any).reports !== undefined) localStorage.setItem('reports', String((data as any).reports));
  if ((data as any).myPharmacy) localStorage.setItem('myPharmacy', String((data as any).myPharmacy));
  if (data.belongsStorage) localStorage.setItem('belongsStorage', data.belongsStorage);
  if (data.cityId) localStorage.setItem('cityId', String(data.cityId));
  if (data.deviceInfo) localStorage.setItem('deviceInfo', data.deviceInfo);
  if (data.targetInterface) localStorage.setItem('targetInterface', data.targetInterface);
  localStorage.setItem('startPage', data.startPage || 'profile');
  if (data.roleLevel !== undefined) localStorage.setItem('roleLevel', String(data.roleLevel));
  if (data.roleName) localStorage.setItem('roleName', data.roleName);
  if (data.permissions) localStorage.setItem('permissions', JSON.stringify(data.permissions));
  if (data.badge) localStorage.setItem('roleBadge', JSON.stringify(data.badge));
  // Признак сотрудника точки пишется и стирается всегда: иначе он перешел бы к следующей учетной записи
  if (data.staffLogin) {
    localStorage.setItem('staffLogin', data.staffLogin);
    localStorage.setItem('staffInfo', JSON.stringify(data.staff || null));
  } else {
    localStorage.removeItem('staffLogin');
    localStorage.removeItem('staffInfo');
  }
  sessionStorage.setItem('openProfileOnLogin', 'true');
};

/** Собственный логин сотрудника точки или null у остальных учетных записей */
export const getStaffLogin = (): string | null => {
  return localStorage.getItem('staffLogin');
};

/** Логин для отображения: у сотрудника точки - свой, а не логин точки из username */
export const getDisplayLogin = (): string => {
  return getStaffLogin() || localStorage.getItem('username') || '';
};

/**
 * Вход администратора под сотрудником. Данные администратора сохраняются снимком целиком и стираются:
 * иначе его аптеки, регион и права остались бы в сессии сотрудника, у которого этих значений нет.
 * returnPath - раздел панели, куда администратор вернется из режима.
 */
export const startImpersonationSession = (targetData: AuthResponse, adminInfo: ImpersonatorInfo, returnPath: string): void => {
  const adminState: Record<string, string> = {};
  ADMIN_SNAPSHOT_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) adminState[key] = value;
  });

  clearUserSessionFully();
  localStorage.setItem(IMPERSONATION_ORIGINAL_KEY, JSON.stringify(adminState));
  localStorage.setItem(IMPERSONATION_RETURN_KEY, returnPath);
  localStorage.setItem('is_impersonated', 'true');
  localStorage.setItem('impersonator_info', JSON.stringify(adminInfo));

  saveUserSession(targetData);
  // Интерфейс сотрудника открывается сразу, без перехода в его профиль
  sessionStorage.removeItem('openProfileOnLogin');
};

const readAdminSnapshot = (): Record<string, unknown> | null => {
  try {
    return JSON.parse(localStorage.getItem(IMPERSONATION_ORIGINAL_KEY) || 'null');
  } catch {
    return null;
  }
};

/**
 * Выход из режима «вход под сотрудником»: данные сотрудника стираются, снимок администратора возвращается.
 * Возвращает раздел, куда вести администратора, или null, если снимка нет - тогда остается экран входа.
 */
export const restoreAdminSession = (): string | null => {
  const snapshot = readAdminSnapshot();
  const returnPath = localStorage.getItem(IMPERSONATION_RETURN_KEY) || '/admin';

  clearUserSessionFully();
  localStorage.removeItem(IMPERSONATION_ORIGINAL_KEY);
  localStorage.removeItem(IMPERSONATION_RETURN_KEY);

  if (!snapshot || typeof snapshot.sessionToken !== 'string') return null;
  ADMIN_SNAPSHOT_KEYS.forEach((key) => {
    const value = snapshot[key];
    if (typeof value === 'string') localStorage.setItem(key, value);
  });
  return returnPath;
};
